import { Component, inject } from '@angular/core';

import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { TranslateService } from '@ngx-translate/core';
import {
  AiAssistant,
  AiAssistantService,
  ASSISTANT_ID_SEO_ANALYST,
  ASSISTANT_ID_VOICE_RECEPTIONIST,
  ConnectionType,
  CUSTOM_ASSISTANT_PLACEHOLDER_ID,
  PlatformLocation,
} from '../../core';
import { AiAssistantCardComponent } from '../ai-assistant-card/ai-assistant-card.component';
import { catchError, combineLatest, firstValueFrom, map, merge, Observable, of, switchMap } from 'rxjs';
import { convertAssistantTypeToDefaultID, getNamespace } from '../../core/services/ai-assistant-utils';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AssistantApiService, AssistantType, UpsertAssistantResponse } from '@vendasta/ai-assistants';
import { SmsDetailsService } from '../../core/services/sms-details.service';
import { MatDialog } from '@angular/material/dialog';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  AI_DEFAULT_WORKFORCE_TOKEN,
  NAMESPACE_CONFIG_TOKEN,
  PARTNER_ID_TOKEN,
  PLATFORM_LOCATION_TOKEN,
  SHOW_CUSTOM_ASSISTANT_TOKEN,
} from '../../core/tokens';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { isNotNullOrUndefined } from '@vendasta/rx-utils';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { AiVoiceTryItModalComponent } from '../ai-voice-try-it-modal/ai-voice-try-it-modal.component';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { MatButton } from '@angular/material/button';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { buildUrlWithQueryParams } from '../../core/utils/navigation.helper';

@Component({
  selector: 'ai-assistant-page',
  imports: [GalaxyPageModule, AiAssistantCardComponent, AiAssistantI18nModule, GalaxyLoadingSpinnerModule, MatButton],
  templateUrl: './ai-assistant-page.component.html',
  styleUrl: './ai-assistant-page.component.scss',
})
export class AiAssistantPageComponent {
  protected readonly aiAssistantService = inject(AiAssistantService);
  protected readonly translateService = inject(TranslateService);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly defaultWorkforce$ = inject(AI_DEFAULT_WORKFORCE_TOKEN);
  private readonly namespaceConfig$ = inject(NAMESPACE_CONFIG_TOKEN);
  private matDialog = inject(MatDialog);
  private readonly router = inject(Router);
  smsDetailsService = inject(SmsDetailsService);
  private readonly phoneNumber$ = this.smsDetailsService.phoneNumber$;
  private readonly isVoiceAIAvailable$ = toObservable(this.aiAssistantService.isVoiceAIAvailable);
  private readonly isAIMarketerAvailable$ = toObservable(this.aiAssistantService.isAIMarketerAvailable);
  private readonly showCustomAssistant$ = inject(SHOW_CUSTOM_ASSISTANT_TOKEN);
  private readonly route = inject(ActivatedRoute);
  private readonly aiIconService = inject(GalaxyAiIconService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly assistantAPI = inject(AssistantApiService);
  private readonly aiWorkforce$ = this.getAiWorkforce();
  private readonly platformLocation = inject(PLATFORM_LOCATION_TOKEN);
  private readonly fetching$ = merge(
    combineLatest([this.partnerId$, this.accountGroupId$]).pipe(map(() => true)),
    this.aiWorkforce$.pipe(map(() => false)),
  );

  protected readonly phoneNumber = toSignal(
    this.smsDetailsService.phoneNumber$.pipe(
      catchError((err) => {
        if (err.status !== 404) {
          this.snackbarService.openErrorSnack('Error getting phone number');
        }
        return of('');
      }),
    ),
  );

  protected readonly showCreateButton = toSignal(
    this.showCustomAssistant$.pipe(
      map((showCustomAssistant) => {
        return this.platformLocation !== PlatformLocation.PLATFORM_LOCATION_ADMIN_CENTER && showCustomAssistant;
      }),
    ),
  );

  protected readonly $fetching = toSignal(this.fetching$, {
    requireSync: true,
  });

  protected readonly aiWorkforce = toSignal(this.aiWorkforce$);

  constructor() {
    this.aiIconService.dummyInit();
  }

  private getAiWorkforce(): Observable<AiAssistant[]> {
    return combineLatest([
      this.isVoiceAIAvailable$,
      this.defaultWorkforce$,
      this.namespaceConfig$,
      this.showCustomAssistant$,
      this.isAIMarketerAvailable$,
    ]).pipe(
      switchMap(
        ([isVoiceAIAvailable, defaultWorkforce, namespaceConfig, showCustomAssistant, isAIMarketerAvailable]) => {
          return this.aiAssistantService.listAllAssistants(namespaceConfig.namespace).pipe(
            map((assistantRes) => {
              let assistants =
                assistantRes
                  ?.map((assistant) =>
                    this.aiAssistantService.hydrateAssistantWithDefaultInfo(assistant, defaultWorkforce),
                  )
                  ?.filter(isNotNullOrUndefined) ?? [];

              assistants = this.processCustomAssistants(assistants, defaultWorkforce, showCustomAssistant);

              defaultWorkforce.forEach((defaultAssistant) => {
                if (defaultAssistant.assistant.id === CUSTOM_ASSISTANT_PLACEHOLDER_ID) {
                  return;
                }
                // Don't display AI Search Expert by default until SEO Analyst placeholder is removed and AI Search Expert goes to GA
                if (defaultAssistant.assistant.type === AssistantType.ASSISTANT_TYPE_AEO_AND_SEO_EXPERT) {
                  return;
                }

                const isAlreadyInList = assistants.find((assistant) => {
                  return (
                    convertAssistantTypeToDefaultID(
                      assistant.assistant.type ?? AssistantType.ASSISTANT_TYPE_UNSPECIFIED,
                    ) === defaultAssistant.assistant.id
                  );
                });

                if (!isAlreadyInList) {
                  // Ensure we're not updating the default assistant by cloning it before
                  const defaultAssistantCopy = this.aiAssistantService.cloneAssistant(defaultAssistant);

                  defaultAssistantCopy.assistant.name = this.translateService.instant(
                    defaultAssistantCopy.assistant.name ?? '',
                  );

                  defaultAssistantCopy.showConfigButtonOnDefault = false;

                  assistants.push(defaultAssistantCopy);
                }
              });
              if (assistants.some((a) => a.assistant.type === AssistantType.ASSISTANT_TYPE_AEO_AND_SEO_EXPERT)) {
                assistants = assistants.filter((a) => a.assistant.id !== ASSISTANT_ID_SEO_ANALYST);
              }

              if (!isVoiceAIAvailable) {
                assistants = assistants.filter((assistant) => {
                  return assistant.assistant.id !== ASSISTANT_ID_VOICE_RECEPTIONIST;
                });
              }

              if (!isAIMarketerAvailable) {
                assistants = assistants.filter((assistant) => {
                  return assistant.assistant.type !== AssistantType.ASSISTANT_TYPE_MARKETER;
                });
              }

              assistants.sort((a, b) => {
                const orderA = a.order ?? 999;
                const orderB = b.order ?? 999;
                return orderA - orderB;
              });

              return assistants;
            }),
            switchMap((assistants) => {
              const connections$ = this.aiAssistantService.listConnections(namespaceConfig.namespace, true);
              return connections$.pipe(
                map((aiConnections) => {
                  return assistants.map((assistant) => {
                    assistant.connections = aiConnections.filter((aiConnection) => {
                      if (assistant.assistant.id === CUSTOM_ASSISTANT_PLACEHOLDER_ID && assistant.isDisabled) {
                        return false;
                      }
                      return (
                        assistant.assistant.type !== undefined &&
                        aiConnection.connection.supportedAssistantTypes?.includes(assistant.assistant.type)
                      );
                    });
                    return assistant;
                  });
                }),
              );
            }),
            map((assistants) =>
              assistants.map((assistant) => {
                if (!assistant.cta && assistant.computeCTA) {
                  assistant.cta = assistant.computeCTA(assistant);
                }

                return assistant;
              }),
            ),
            map((assistants) => this.addVoiceReceptionistCTA(assistants)),
            catchError((e) => {
              console.error('error getting AI workforce', e);
              return of([]);
            }),
          );
        },
      ),
    );
  }

  private processCustomAssistants(
    assistants: AiAssistant[],
    defaultWorkforce: AiAssistant[],
    showCustomAssistant: boolean,
  ): AiAssistant[] {
    const processedAssistants = !showCustomAssistant
      ? assistants.map((assistant) =>
          assistant.assistant.type === AssistantType.ASSISTANT_TYPE_CUSTOM
            ? { ...assistant, isDisabled: true }
            : assistant,
        )
      : assistants;

    const hasRealCustomAssistants = processedAssistants.some(
      (assistant) =>
        assistant.assistant.type === AssistantType.ASSISTANT_TYPE_CUSTOM &&
        assistant.assistant.id !== CUSTOM_ASSISTANT_PLACEHOLDER_ID,
    );

    if (hasRealCustomAssistants) {
      return processedAssistants.filter((assistant) => assistant.assistant.id !== CUSTOM_ASSISTANT_PLACEHOLDER_ID);
    }

    const placeholder = this.createCustomAssistantPlaceholder(defaultWorkforce, showCustomAssistant);
    return placeholder ? [...processedAssistants, placeholder] : processedAssistants;
  }

  private createCustomAssistantPlaceholder(
    defaultWorkforce: AiAssistant[],
    showCustomAssistant: boolean,
  ): AiAssistant | null {
    const placeholderAssistant = defaultWorkforce.find(
      (assistant) => assistant.assistant.id === CUSTOM_ASSISTANT_PLACEHOLDER_ID,
    );

    if (!placeholderAssistant) {
      return null;
    }

    const placeholderCopy = this.aiAssistantService.cloneAssistant(placeholderAssistant);
    placeholderCopy.assistant.name = this.translateService.instant(placeholderCopy.assistant.name ?? '');

    if (!showCustomAssistant) {
      placeholderCopy.isDisabled = true;
    } else {
      placeholderCopy.cta = {
        label: 'AI_ASSISTANT.WORKFORCE.CREATE_ASSISTANT',
        action: {
          callback: () => this.createAssistant(),
          showButton: true,
        },
      };
    }

    return placeholderCopy;
  }

  private addVoiceReceptionistCTA(assistants: AiAssistant[]): AiAssistant[] {
    return assistants.map((assistant) => {
      if (assistant.assistant.id === ASSISTANT_ID_VOICE_RECEPTIONIST && !assistant.cta && !assistant.isDisabled) {
        const clonedAssistant = this.aiAssistantService.cloneAssistant(assistant);
        if (this.phoneNumber()) {
          clonedAssistant.cta = {
            label: 'AI_ASSISTANT.WORKFORCE.TRY_IT',
            action: {
              callback: this.openVoiceReceptionistTryItModal.bind(this),
              showButton: true,
            },
          };
        } else if (
          !this.phoneNumber() &&
          clonedAssistant.connections?.some(
            (connection) => connection.connection.connectionType === ConnectionType.Voice,
          )
        ) {
          clonedAssistant.cta = {
            label: 'INBOX.SETTINGS.SMS_MESSAGES.CARD.CLAIM_PHONE_NUMBER',
            action: this.platformLocation === PlatformLocation.PLATFORM_LOCATION_PARTNER_CENTER
              ? { pathCommands: ['/inbox/settings/'], showButton: true }
              : {
                  callback: async () => {
                    const accountGroupId = await firstValueFrom(this.accountGroupId$);
                    this.router.navigate(['/account/location', accountGroupId, 'settings', 'inbox', 'sms-registration']);
                  },
                  showButton: true,
                },
          };
        }
        return clonedAssistant;
      }
      return assistant;
    });
  }

  protected async openVoiceReceptionistTryItModal(): Promise<void> {
    this.matDialog.open(AiVoiceTryItModalComponent);
  }

  protected triggerAction(aiAssistant: AiAssistant): void {
    if (aiAssistant) {
      this.buildAssistantActionCallback(aiAssistant)?.();
    }
  }

  protected buildAssistantActionCallback(aiAssistant: AiAssistant): (() => void) | undefined {
    if (!aiAssistant?.cta || !aiAssistant?.cta.action?.showButton) {
      return undefined;
    }

    const action = aiAssistant?.cta?.action;
    if (action?.url) {
      if (action.newTab && !action.relativeRoute) {
        const finalUrl = buildUrlWithQueryParams(action.url, action.queryParams);
        return () => window.open(finalUrl, '_blank');
      }

      const navigationExtras: NavigationExtras = action.relativeRoute ? { relativeTo: this.route } : {};
      if (action.queryParams) {
        navigationExtras.queryParams = action.queryParams;
      }

      return () => this.router.navigate([action.url], navigationExtras);
    }

    if (action?.pathCommands) {
      return () => this.router.navigate(action.pathCommands);
    }

    if (action?.callback) {
      return action.callback;
    }
    return undefined;
  }

  protected async createAssistant(): Promise<void> {
    try {
      const partnerId = await firstValueFrom(this.partnerId$);
      const accountGroupId = await firstValueFrom(this.accountGroupId$);
      const namespace = getNamespace(accountGroupId, partnerId);

      if (!namespace) {
        this.snackbarService.openErrorSnack(
          'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.CUSTOM_ASSISTANT.ERRORS.CREATE_FAILED',
        );
        return;
      }

      // Create the assistant
      const createResponse: UpsertAssistantResponse = await firstValueFrom(
        this.assistantAPI.upsertAssistant({
          assistant: {
            namespace,
            type: AssistantType.ASSISTANT_TYPE_CUSTOM,
          },
          options: {
            applyDefaults: true,
          },
        }),
      );

      // Navigate to the configuration page
      const configUrl = await firstValueFrom(
        this.aiAssistantService.buildAssistantConfigurationUrl(createResponse.assistant.id || '', true),
      );
      await this.router.navigate([configUrl]);
    } catch (error) {
      console.error('Error creating assistant:', error);
      this.snackbarService.openErrorSnack(
        'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.CUSTOM_ASSISTANT.ERRORS.CREATE_FAILED',
      );
    }
  }
}
